import type { SpeakerNotesSlideContent } from "../../../types";
import { getErrorMessage, logSpeakerNotesError } from "./speakerNotesApi";

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/** Reads every shape's text on the selected slide into a single newline-joined string. */
async function extractSelectedSlideText(): Promise<string> {
  if (!hasPpt()) return "";
  try {
    return await new Promise<string>((resolve) => {
      ppt()
        .run(async (context) => {
          const selected = context.presentation.getSelectedSlides();
          selected.load("items");
          await context.sync();
          const slide = selected.items[0];
          if (!slide) {
            resolve("");
            return;
          }
          slide.shapes.load("items");
          await context.sync();
          const texts: string[] = [];
          for (const shape of slide.shapes.items) {
            try {
              const tf = shape.textFrame;
              tf.load("textRange/text");
              await context.sync(); // NOSONAR ordered Office.js operations on a shared context
              if (tf.textRange?.text) texts.push(tf.textRange.text);
            } catch {
              /* shape may not have a textFrame */
            }
          }
          resolve(texts.join("\n"));
        })
        .catch(() => resolve(""));
    });
  } catch {
    return "";
  }
}

interface RawTableCell {
  body: { textRange: PowerPoint.TextRange };
}

interface RawTableShape {
  rowCount: number;
  columnCount: number;
  getCell(row: number, col: number): RawTableCell;
  load(s: string): void;
}

function shapeTypeOf(shape: PowerPoint.Shape): string {
  return String((shape as unknown as { type?: string }).type ?? "").toLowerCase();
}

function shapeTable(shape: PowerPoint.Shape): RawTableShape {
  return (shape as unknown as { table: RawTableShape }).table;
}

/** Build one row x col grid of empty strings per table shape, to be filled in by cell text below. */
function buildEmptyTables(tableShapes: PowerPoint.Shape[]): string[][][] {
  return tableShapes.map((shape) => {
    const tbl = shapeTable(shape);
    const table: string[][] = [];
    for (let row = 0; row < tbl.rowCount; row++) {
      table.push(new Array<string>(tbl.columnCount).fill(""));
    }
    return table;
  });
}

interface TableCellRange {
  table: number;
  row: number;
  col: number;
  range: PowerPoint.TextRange;
}

interface MalformedCell {
  table: number;
  row: number;
  col: number;
}

/**
 * Load every cell's text range across all table shapes. A cell throws when
 * PowerPoint reports it as merged/malformed and its textRange can't be
 * addressed individually — those are skipped (left blank later) and their
 * locations are reported once as a single summary, so a systematic failure
 * is still visible (with enough context to find it) without spamming
 * telemetry for large tables.
 */
function collectTableCellRanges(tableShapes: PowerPoint.Shape[]): {
  cellRanges: TableCellRange[];
  malformedCells: MalformedCell[];
} {
  const cellRanges: TableCellRange[] = [];
  const malformedCells: MalformedCell[] = [];
  for (const [tableIndex, shape] of tableShapes.entries()) {
    const tbl = shapeTable(shape);
    for (let row = 0; row < tbl.rowCount; row++) {
      for (let col = 0; col < tbl.columnCount; col++) {
        try {
          const range = tbl.getCell(row, col).body.textRange;
          range.load("text");
          cellRanges.push({ table: tableIndex, row, col, range });
        } catch {
          malformedCells.push({ table: tableIndex, row, col });
        }
      }
    }
  }
  return { cellRanges, malformedCells };
}

/** Reads every table on the selected slide into a plain string grid, within an already-open PowerPoint.run batch. */
async function extractTablesFromContext(context: PowerPoint.RequestContext): Promise<string[][][]> {
  const selected = context.presentation.getSelectedSlides();
  selected.load("items");
  await context.sync();
  const slide = selected.items[0];
  if (!slide) return [];

  slide.shapes.load("items");
  await context.sync();
  const tableShapes = slide.shapes.items.filter((s) => shapeTypeOf(s) === "table");
  if (tableShapes.length === 0) return [];

  for (const shape of tableShapes) {
    shapeTable(shape).load("rowCount,columnCount");
  }
  await context.sync();

  const { cellRanges, malformedCells } = collectTableCellRanges(tableShapes);
  if (malformedCells.length > 0) {
    const locations = malformedCells.map((cell) => `table ${cell.table} r${cell.row}c${cell.col}`).join(", ");
    logSpeakerNotesError(
      "speaker-notes-extract-table-cells",
      `${malformedCells.length} table cell(s) could not be read: ${locations}`,
    );
  }
  await context.sync();

  const tables = buildEmptyTables(tableShapes);
  for (const { table, row, col, range } of cellRanges) {
    tables[table]![row]![col] = range.text ?? "";
  }
  return tables;
}

/**
 * Reads every table on the selected slide into a plain string grid, so the
 * backend prompt (DeckorateAPI's `render_slide_content`) can render rows as
 * tab-separated text alongside the slide's free text.
 */
export async function extractSelectedSlideTables(): Promise<string[][][]> {
  if (!hasPpt()) {
    // Should never happen — SpeakerNotes only runs once the PowerPoint host is
    // already loaded — so treat it the same as the two failure paths below.
    logSpeakerNotesError("speaker-notes-extract-tables", "PowerPoint host API is unavailable");
    return [];
  }
  try {
    return await new Promise<string[][][]>((resolve) => {
      ppt()
        .run(async (context) => resolve(await extractTablesFromContext(context)))
        .catch((error: unknown) => {
          logSpeakerNotesError("speaker-notes-extract-tables", getErrorMessage(error, "table extraction failed"));
          resolve([]);
        });
    });
  } catch (error) {
    logSpeakerNotesError("speaker-notes-extract-tables", getErrorMessage(error, "table extraction failed"));
    return [];
  }
}

/** Builds the SpeakerNotesRequest.slide_content payload from the selected slide. */
export async function extractSelectedSlideContent(): Promise<SpeakerNotesSlideContent> {
  const [textContent, tables] = await Promise.all([
    extractSelectedSlideText(),
    extractSelectedSlideTables(),
  ]);
  return {
    textContent,
    shapes: tables.map((tableData) => ({ tableData })),
  };
}
